import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetch } from '../services/api';
import { hasFeature } from '../utils/featureFlags';
import { isAdminMonitor } from '../utils/adminMonitor';

function loadPermissions(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem("catms_permissions") ?? "{}"); }
  catch { return {}; }
}
function canViewCrew(): boolean {
  const p = loadPermissions();
  // まだ権限取得前（空オブジェクト）は非表示
  return p.view_crew_attendance === true;
}
/** 陸上勤務者（ポータルの会社の陸上・システム管理者）か。モバイルポータルホームのタブを出す */
function isOfficeUser(): boolean {
  if (!hasFeature('portal')) return false;
  try {
    const u = JSON.parse(localStorage.getItem('catms_user') ?? '{}') as { user_role?: string };
    return ['shore_admin', 'system_admin'].includes(u?.user_role ?? '');
  } catch { return false; }
}

/** 陸上勤務者用のタブ（船員用の打刻・履歴・乗組員は見せず、ポータル中心にする） */
const OFFICE_TABS = [
  { path: '/portal-home', label: 'ホーム',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
  { path: '/portal-board', label: '連絡',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg> },
  { path: '/portal-chat', label: 'チャット',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
  { path: '/portal-requests', label: '申請',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="m9 15 2 2 4-4"/></svg> },
  { path: '/settings', label: '設定',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06-.06a2 2 0 0 1-2.83-2.83l.06.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg> },
];

/** 管理者の確認専用モード（crew_admin_monitor）: 打刻・履歴を隠し、確認・連絡に絞る */
const ADMIN_TABS = [
  { path: '/crew-status', label: '状況',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
  { path: '/alerts',      label: 'アラート',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg> },
  { path: '/messages',    label: '連絡',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
  { path: '/settings',    label: '設定',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06-.06a2 2 0 0 1-2.83-2.83l.06.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg> },
];

const TABS = [
  { path: '/home',        label: '打刻',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
  { path: '/history',     label: '履歴',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg> },
  { path: '/crew-status', label: '乗組員',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
  { path: '/messages',    label: '連絡',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
  { path: '/alerts',      label: 'アラート',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg> },
  { path: '/settings',    label: '設定',
    svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06-.06a2 2 0 0 1-2.83-2.83l.06.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg> },
];

export const BottomNav: React.FC = () => {
  const nav = useNavigate();
  const loc = useLocation();
  // 権限取得完了後に再描画するため state で管理
  const [showCrew, setShowCrew] = useState(canViewCrew);
  const [unreadDms, setUnreadDms] = useState(0);
  const [portalUnread, setPortalUnread] = useState<{ board: number; chat: number }>({ board: 0, chat: 0 });
  const [adminMon, setAdminMon] = useState(isAdminMonitor);
  const [alertCount, setAlertCount] = useState(0);
  const office = isOfficeUser();

  useEffect(() => {
    // storage イベントで他タブの変更にも対応
    const handler = () => { setShowCrew(canViewCrew()); setAdminMon(isAdminMonitor()); };
    window.addEventListener("storage", handler);
    // 定期的に確認（App.tsx の非同期フェッチ完了を拾う）
    const timer = setInterval(() => { setShowCrew(canViewCrew()); setAdminMon(isAdminMonitor()); }, 500);
    return () => { window.removeEventListener("storage", handler); clearInterval(timer); };
  }, []);

  // 管理者の確認専用モード: 未確認アラート件数をバッジに出す
  useEffect(() => {
    if (!adminMon) return;
    const fetchCount = () => {
      apiFetch<{ data: { total: number } }>('/alerts/count')
        .then(r => setAlertCount(r.data?.total ?? 0))
        .catch(() => {});
    };
    fetchCount();
    const t = setInterval(fetchCount, 45_000);
    return () => clearInterval(t);
  }, [adminMon, loc.pathname]);

  // 未読DM件数を定期取得（メッセージページ表示中は非表示）
  useEffect(() => {
    const fetchUnread = () => {
      if (loc.pathname === '/messages') { setUnreadDms(0); return; }
      apiFetch<{ data: { dms: number } }>('/messages/unread-count')
        .then(r => setUnreadDms(r.data?.dms ?? 0))
        .catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30_000);
    return () => clearInterval(t);
  }, [loc.pathname]);

  // 陸上勤務者: ポータルの未読（連絡・チャット）をタブのバッジに出す
  useEffect(() => {
    if (!office) return;
    const fetchPortal = () => {
      apiFetch<{ data: { board_unread: number; chat_unread: number } }>('/portal/summary', {}, { noRedirect: true })
        .then(r => setPortalUnread({ board: r.data?.board_unread ?? 0, chat: r.data?.chat_unread ?? 0 }))
        .catch(() => {});
    };
    fetchPortal();
    const t = setInterval(fetchPortal, 45_000);
    return () => clearInterval(t);
  }, [office]);

  // 陸上勤務者はポータル中心のタブ、管理者（確認専用モード）は確認に絞ったタブ、船員は従来のタブ
  const visibleTabs = office
    ? OFFICE_TABS
    : adminMon
    ? ADMIN_TABS
    : TABS.filter(t => t.path !== '/crew-status' || showCrew);

  return (
    <nav className="app-bottom-safe" style={{
      flexShrink: 0, background: 'white',
      borderTop: '1px solid #e4e9f0',
      display: 'flex', justifyContent: 'space-around', alignItems: 'center',
      padding: '8px 0 0',
    }}>
      {visibleTabs.map(t => {
        const active = loc.pathname === t.path ||
          (t.path === '/crew-status' && loc.pathname.startsWith('/crew-status'));
        const badge =
          t.path === '/messages' ? unreadDms :
          t.path === '/alerts' && adminMon ? alertCount :
          t.path === '/portal-board' ? portalUnread.board :
          t.path === '/portal-chat' ? portalUnread.chat : 0;
        return (
          <button key={t.path} onClick={() => nav(t.path)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            color: active ? (office ? '#0f766e' : '#0057d9') : '#6b7280',
            fontSize: 10, fontWeight: active ? 800 : 500,
            padding: '4px 8px', flex: 1,
          }}>
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              {t.svg}
              {badge > 0 && (
                <div style={{
                  position: 'absolute', top: -5, right: -7,
                  background: '#ef4444', color: 'white',
                  fontSize: 9, fontWeight: 800, borderRadius: '50%',
                  minWidth: 16, height: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 3px', lineHeight: 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }}>
                  {badge > 9 ? '9+' : badge}
                </div>
              )}
            </div>
            <span style={{ whiteSpace: 'nowrap' }}>{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomNav;
